import type { components } from '@/api/schema';

import { keysOf, labelOf } from './humanize';

type Schemas = components['schemas'];

export type OptionTier = Schemas['ProposalOptionTotal']['optionTier'];
export type CustomerDecision = Schemas['PublicProposalDecision']['decision'];
export type FindingSeverity = Schemas['ProposalFinding']['severity'];

/** Where a proposal stands; the schema leaves it open. */
export type ProposalStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'PRESENTED' | 'ACCEPTED' | 'DECLINED';

/** What a manager decided about a price exception; the schema leaves it open. */
export type ExceptionStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type TemplateStatus = 'DRAFT' | 'PUBLISHED' | 'RETIRED';

/** What the pricing and terms controls can find wrong with a version; the schema leaves it open. */
export type ProposalFindingCode =
  | 'EXPIRED_PRICING'
  | 'PRICE_DRIFT'
  | 'WITHDRAWN_ITEM'
  | 'LAPSED_REBATE'
  | 'BELOW_GP_FLOOR'
  | 'BELOW_GP_TARGET'
  | 'OFF_COOKBOOK'
  | 'OMITTED_TERMS'
  | 'UNAPPROVED_TERMS'
  | 'ALTERED_TERMS'
  | 'INCONSISTENT_TOTALS'
  | 'UNDECLARED_TOTAL';

/** What changed between two versions; the schema leaves it open. */
export type ProposalChangeKind =
  | 'TAX_CHANGED'
  | 'TEMPLATE_CHANGED'
  | 'LINE_ADDED'
  | 'LINE_CHANGED'
  | 'LINE_REMOVED'
  | 'TERM_ADDED'
  | 'TERM_CHANGED'
  | 'TERM_REMOVED';

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Waiting for manager approval',
  PRESENTED: 'Presented',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
};

// Good, better, best is the sales team's shorthand; the customer and the screen say these.
export const OPTION_TIER_LABELS: Record<OptionTier, string> = {
  GOOD: 'Essential',
  BETTER: 'Recommended',
  BEST: 'Complete',
};

export const PROPOSAL_FINDING_LABELS: Record<ProposalFindingCode, string> = {
  EXPIRED_PRICING: 'A price has expired or is not yet in effect',
  PRICE_DRIFT: 'A price no longer matches the pricebook',
  WITHDRAWN_ITEM: 'An item is no longer approved to sell',
  LAPSED_REBATE: 'A rebate in the terms has ended',
  BELOW_GP_FLOOR: 'Below the minimum margin',
  BELOW_GP_TARGET: 'Below the target margin',
  OFF_COOKBOOK: 'Priced outside the pricing policy',
  OMITTED_TERMS: 'A required terms section is missing',
  UNAPPROVED_TERMS: 'A terms section is not in the approved template',
  ALTERED_TERMS: 'Terms differ from the approved wording',
  INCONSISTENT_TOTALS: 'An option total does not match its lines',
  UNDECLARED_TOTAL: 'An option total was not stated',
};

export const FINDING_SEVERITY_LABELS: Record<FindingSeverity, string> = {
  BLOCKING: 'Must be fixed or approved',
  ADVISORY: 'Worth knowing',
};

export const PROPOSAL_CHANGE_LABELS: Record<ProposalChangeKind, string> = {
  TAX_CHANGED: 'Tax rate changed',
  TEMPLATE_CHANGED: 'Template changed',
  LINE_ADDED: 'Line added',
  LINE_CHANGED: 'Line changed',
  LINE_REMOVED: 'Line removed',
  TERM_ADDED: 'Terms added',
  TERM_CHANGED: 'Terms changed',
  TERM_REMOVED: 'Terms removed',
};

export const EXCEPTION_STATUS_LABELS: Record<ExceptionStatus, string> = {
  PENDING: 'Waiting for a manager',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const TEMPLATE_STATUS_LABELS: Record<TemplateStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  RETIRED: 'Retired',
};

export const CUSTOMER_DECISION_LABELS: Record<CustomerDecision, string> = {
  ACCEPT: 'Accepted',
  DECLINE: 'Declined',
  NEEDS_MORE_INFO: 'Asked for more information',
};

/** The three option tiers, cheapest first. */
export const OPTION_TIERS = keysOf(OPTION_TIER_LABELS);

export function proposalStatusLabel(status: string | null | undefined): string {
  return labelOf(PROPOSAL_STATUS_LABELS, status);
}

export function optionTierLabel(tier: string | null | undefined): string {
  return labelOf(OPTION_TIER_LABELS, tier);
}

export function proposalFindingLabel(code: string | null | undefined): string {
  return labelOf(PROPOSAL_FINDING_LABELS, code);
}

export function findingSeverityLabel(severity: string | null | undefined): string {
  return labelOf(FINDING_SEVERITY_LABELS, severity);
}

export function proposalChangeLabel(kind: string | null | undefined): string {
  return labelOf(PROPOSAL_CHANGE_LABELS, kind);
}

export function exceptionStatusLabel(status: string | null | undefined): string {
  return labelOf(EXCEPTION_STATUS_LABELS, status);
}

export function templateStatusLabel(status: string | null | undefined): string {
  return labelOf(TEMPLATE_STATUS_LABELS, status);
}

export function customerDecisionLabel(decision: string | null | undefined): string {
  return labelOf(CUSTOMER_DECISION_LABELS, decision);
}
